import type { Href } from "expo-router";
import { StyleSheet } from "react-native-unistyles";
import { Text, View } from "@src/components";
import {
  activationCameraKey,
  callFacts,
  calledOn,
  callTitle,
  describeIncidentTimeline,
  incidentTimelineSpan,
  type DescribedIncidentStep,
  type IncidentActivation,
  type IncidentCall,
  type IncidentDetail,
  type IncidentTimelineRail,
} from "@visioni/a3s-monitoring/app";
import { EMPTY, formatInstant, formatText } from "../format";
import { Empty, Field, InlineLink, Section } from "./DetailSection";

interface IncidentDetailViewProps {
  readonly incident: IncidentDetail;
  readonly tenantLabel: string;
  readonly siteLabel: (siteId: string) => string;
  readonly cameraLabel: (cameraKey: string) => string;
  /** The activation page, whose operator notes include every call made on it; with `noteId`, opened at that note. */
  readonly activationHref: (ticketNo: string, noteId?: string) => Href;
  /** The Patriot client's page. */
  readonly clientHref: Href;
  /** The fleet incident list narrowed to this incident's client. */
  readonly clientIncidentsHref: Href;
  /** Where a call outside any activation can be read: no activation page holds its note. */
  readonly ticketlessCallHref: (call: IncidentCall) => Href;
  /**
   * The organisation's incident list, narrowed to a site or a camera at it;
   * absent where the viewer has no such list to open, and the fields are text.
   */
  readonly tenantIncidentsHref?: ((scope: { readonly site?: string; readonly camera?: string }) => Href) | undefined;
}

/**
 * What happened in an incident, as a timeline from the rule's own stored
 * evidence that says why each step is in it, then the calls and activations it
 * is made of. Who was called, and which operator did each step, come from the
 * operator notes when the replica holds them; each call links to its note, on
 * the activation's page or, for a call outside any activation, in the client's
 * operator notes.
 */
export function IncidentDetailView({
  incident,
  tenantLabel,
  siteLabel,
  cameraLabel,
  activationHref,
  clientHref,
  clientIncidentsHref,
  ticketlessCallHref,
  tenantIncidentsHref,
}: IncidentDetailViewProps) {
  const cameraKey = activationCameraKey(incident);
  const span = incidentTimelineSpan(incident);
  const site = incident.a3sSite;

  return (
    <View gap="4xl">
      <Section title="Summary">
        <Field label="Started">{formatInstant(span.startedAt)}</Field>
        <Field label="First call">{formatInstant(span.firstCallAt)}</Field>
        <Field label="Ended">{formatInstant(span.endedAt)}</Field>
        <Field label="Police">{incident.policeCalled ? "Called" : "Not called"}</Field>
        <Field label="Status">
          {incident.settled ? "Settled" : "Open: more calls or activations may still join it"}
        </Field>
        <Field label="Patriot client">
          <View flexDirection="row" gap="lg" flexWrap="wrap">
            <InlineLink href={clientHref}>{incident.clientNo}</InlineLink>
            <InlineLink href={clientIncidentsHref}>Other incidents for this client</InlineLink>
          </View>
        </Field>
        <Field label="Organisation">{scopeLink(tenantLabel, tenantIncidentsHref?.({}))}</Field>
        <Field label="Site">
          {site !== undefined ? scopeLink(siteLabel(site), tenantIncidentsHref?.({ site })) : EMPTY}
        </Field>
        <Field label="Camera">
          {cameraKey !== undefined ?
            scopeLink(
              cameraLabel(cameraKey),
              // The list filters a camera only within its site.
              site !== undefined ? tenantIncidentsHref?.({ site, camera: cameraKey }) : undefined,
            )
          : EMPTY}
        </Field>
      </Section>

      <Section title="Timeline">
        <Text textStyle="sm/regular" color="textMuted">
          {TIMELINE_INTRO}
        </Text>
        <View style={styles.timeline}>
          {describeIncidentTimeline(incident).map((row, index) => (
            <View key={String(index)} flexDirection="row">
              {row.rails.map((rail, lane) => (
                <Rail key={String(lane)} rail={rail} />
              ))}
              <View style={styles.step}>
                <TimelineStep
                  step={row.step}
                  href={row.step.ticketNo !== undefined ? activationHref(row.step.ticketNo) : undefined}
                />
              </View>
            </View>
          ))}
        </View>
      </Section>

      <Section title={`Calls (${String(incident.calls.length)})`}>
        {incident.calls.length === 0 ?
          <Empty>No calls.</Empty>
        : incident.calls.map((call, index) => (
            <CallRow
              key={call.noteId}
              call={call}
              isFirst={index === 0}
              href={call.ticketNo !== undefined ? activationHref(call.ticketNo, call.noteId) : ticketlessCallHref(call)}
            />
          ))
        }
      </Section>

      <Section title={`Activations (${String(incident.activations.length)})`}>
        {incident.activations.length === 0 ?
          <Empty>The calls were made outside any activation.</Empty>
        : incident.activations.map((activation, index) => (
            <ActivationRow
              key={activation.ticketNo}
              activation={activation}
              incident={incident}
              isFirst={index === 0}
              href={activationHref(activation.ticketNo)}
            />
          ))
        }
      </Section>
    </View>
  );
}

/** A summary value that opens the incident list it scopes, where there is one; plain text otherwise. */
const scopeLink = (label: string, href: Href | undefined) =>
  href !== undefined ? <InlineLink href={href}>{label}</InlineLink> : label;

/**
 * Genuine incidents are new to the business: what one is, above every timeline,
 * so a reader can see why each step is in it without asking. Kept free of the
 * rule's thresholds, which are still being tuned.
 */
const TIMELINE_INTRO =
  "An incident starts when an operator makes an outbound call. Calls and activations for this client close to it in time are grouped with it, and each step below says why it's included.";

/**
 * One lane of a timeline row: the activation's rail, with a mark where the
 * step was made on it, or nothing where no activation is open in the lane.
 */
function Rail({ rail }: { rail: IncidentTimelineRail | undefined }) {
  return (
    <View style={styles.lane}>
      {rail?.fromAbove === true && <View style={[styles.rail, styles.railAbove]} />}
      {rail?.toBelow === true && <View style={[styles.rail, styles.railBelow]} />}
      {rail?.on === true && <View style={styles.mark} />}
    </View>
  );
}

/** A step links to the activation its title names. */
function TimelineStep({ step, href }: { step: DescribedIncidentStep; href?: Href | undefined }) {
  return (
    <View gap="xs">
      <Text textStyle="xs/regular" color="textMuted">
        {withOperator(formatInstant(step.at), step.operator)}
      </Text>
      {href !== undefined ?
        <InlineLink href={href}>{step.title}</InlineLink>
      : <Text textStyle="sm/semibold" color="text">
          {step.title}
        </Text>
      }
      {step.facts?.map((fact) => (
        <Text key={fact} textStyle="sm/regular" color="text">
          {fact}
        </Text>
      ))}
      {step.detail !== undefined && (
        <Text textStyle="sm/regular" color="textMuted">
          {step.detail}
        </Text>
      )}
    </View>
  );
}

const withOperator = (time: string, operator: string | undefined): string =>
  operator === undefined ? time : `${time} · ${operator}`;

function CallRow({ call, isFirst, href }: { call: IncidentCall; isFirst: boolean; href: Href }) {
  return (
    <View style={[styles.entry, !isFirst && styles.divider]}>
      <Text textStyle="xs/regular" color="textMuted">
        {withOperator(formatInstant(call.at), call.note?.operatorLogin)}
      </Text>
      <Text textStyle="sm/semibold" color="text">
        {callTitle(call)}
      </Text>
      {callFacts(call).map((fact) => (
        <Text key={fact} textStyle="sm/regular" color="text">
          {fact}
        </Text>
      ))}
      <InlineLink href={href}>
        {call.ticketNo !== undefined ?
          `Note ${call.noteId} on activation ${call.ticketNo}`
        : `Note ${call.noteId}, outside any activation`}
      </InlineLink>
    </View>
  );
}

function ActivationRow({
  activation,
  incident,
  isFirst,
  href,
}: {
  activation: IncidentActivation;
  incident: IncidentDetail;
  isFirst: boolean;
  href: Href;
}) {
  return (
    <View style={[styles.entry, !isFirst && styles.divider]}>
      <InlineLink href={href}>{`Activation ${activation.ticketNo}`}</InlineLink>
      <Field label="Opened">{formatInstant(activation.openedAt)}</Field>
      <Field label="Closed">{activation.closedAt !== undefined ? formatInstant(activation.closedAt) : "Open"}</Field>
      <Field label="Picked up by">
        {formatText(activation.pickUps?.map((pickUp) => pickUp.operatorLogin ?? "Unknown").join(", "))}
      </Field>
      <Field label="Completed by">{formatText(activation.completedBy)}</Field>
      <Field label="Reason">{formatText(activation.reason)}</Field>
      <Field label="Included because">
        {activation.link.kind === "called" ? calledOn(activation, incident) : "Open near a call"}
      </Field>
    </View>
  );
}

/** Where a rail's mark sits: level with the middle of the step's first line, its time. */
const MARK_CENTRE = 8;
const MARK_SIZE = 10;
const RAIL_WIDTH = 2;

const styles = StyleSheet.create((theme) => ({
  entry: { gap: theme.spacing.xs, paddingTop: theme.spacing.md },
  divider: { borderTopWidth: 1, borderColor: theme.colors.borderCard },
  timeline: { paddingTop: theme.spacing.md },
  step: { flex: 1, paddingLeft: theme.spacing.sm, paddingBottom: theme.spacing.lg },
  lane: { width: theme.spacing["3xl"], alignItems: "center" },
  rail: { position: "absolute", width: RAIL_WIDTH, backgroundColor: theme.colors.borderCard },
  railAbove: { top: 0, height: MARK_CENTRE },
  railBelow: { top: MARK_CENTRE, bottom: 0 },
  mark: {
    position: "absolute",
    top: MARK_CENTRE - MARK_SIZE / 2,
    width: MARK_SIZE,
    height: MARK_SIZE,
    borderRadius: MARK_SIZE / 2,
    backgroundColor: theme.colors.textMuted,
  },
}));
